/**
 * The pure half of `LocationPicker` (#472): one searchable select over
 * addresses, floors, rooms and doors, each level grouped by the level above it.
 * Grown from the signage board scope picker (addresses + floors in one
 * grouped MultiSelect), which is now its `['address','floor']` multi mode.
 *
 * - Values are encoded `a:<id>` / `f:<id>` / `r:<id>` / `d:<id>` so one
 *   (Multi)Select holds every level — the board scope's encoding, plus rooms
 *   and doors. `door` is opt-in: only a caller that lists it in `kinds` sees doors.
 * - A picked floor means the floor itself, never "all its rooms": that is
 *   how announcement and board scopes are stored.
 * - Rows read short inside their group ("Floor 6", "Townhouse 26605"); the
 *   closed input and the pills read in full: "Lunta Street 26, Floor 6",
 *   "Townhouse · 26605 — Lunta Street 26, Floor 6".
 */

/** A floor's own name as the pickers show it: a bare number ("4", "-1")
 *  reads "Floor 4"; a named floor ("Entré", "Floor 7") is kept. */
export function floorDisplayName(name: string): string {
  const trimmed = name.trim();
  return /^-?\d+[A-Za-z]?$/.test(trimmed) ? `Floor ${trimmed}` : trimmed;
}

export const PICKER_KINDS = ['address', 'floor', 'room', 'door'] as const;
export type PickerKind = (typeof PICKER_KINDS)[number];

/** A picked location: its level and the row id. */
export interface LocationRef {
  kind: PickerKind;
  id: number;
}

interface AddressLike {
  id: number | string;
  name: string;
  /** "L26" — what members read; "" or absent = use the name. */
  short_name?: string | null;
}

export type PickerAddress = AddressLike;

/** A floor as `/crm/floors/` lists it (its address nested). */
export interface PickerFloor {
  id: number | string;
  name: string;
  address?: AddressLike | null;
  address_id?: number | string | null;
}

/** A room as `/crm/rooms/` lists it: `name` is its number ("26605"),
 *  `description` what people call it ("Townhouse"). */
export interface PickerRoom {
  id: number | string;
  name: string;
  description?: string | null;
  floor?: { id: number | string; name: string; address?: AddressLike | null } | null;
}

/** A door as `/crm/doors/` lists it: it sits on a floor, in a room (whose floor
 *  is then the floor it is on) and at an address; each can be missing. */
export interface PickerDoor {
  id: number | string;
  name: string;
  description?: string | null;
  address?: AddressLike | null;
  floor?: { id: number | string; name: string; address?: AddressLike | null } | null;
  room?: {
    id: number | string;
    name: string;
    description?: string | null;
    floor?: { id: number | string; name: string; address?: AddressLike | null } | null;
  } | null;
}

export interface LocationOption {
  /** The encoded select value (`r:12`). */
  value: string;
  kind: PickerKind;
  id: number;
  /** The full text: the closed input and the multi-select pills. */
  label: string;
  /** The row's own text inside its group. */
  primary: string;
  /** Muted text after it (a room's number when it has a description). */
  secondary?: string;
  /** Indent inside the group: rooms sit under their floor row in a tree. */
  depth: 0 | 1;
  /** The encoded value of the row above it in the tree, if any. */
  parent?: string;
  /** Lower-cased text the search matches against. */
  search: string;
}

export interface LocationOptionGroup {
  /** The heading; '' for the flat address list. */
  group: string;
  items: LocationOption[];
}

export interface LocationPickerData {
  addresses?: readonly PickerAddress[];
  floors?: readonly PickerFloor[];
  rooms?: readonly PickerRoom[];
  /** Only read when `kinds` includes `door`. */
  doors?: readonly PickerDoor[];
}

const PREFIX: Record<PickerKind, string> = { address: 'a', floor: 'f', room: 'r', door: 'd' };
const KIND_OF_PREFIX: Record<string, PickerKind> = { a: 'address', f: 'floor', r: 'room', d: 'door' };

/** `{ kind: 'room', id: 12 }` → `r:12`. */
export const locationValue = (ref: LocationRef): string => `${PREFIX[ref.kind]}:${ref.id}`;

/** `r:12` → `{ kind: 'room', id: 12 }`; null for anything else. */
export function parseLocationValue(value: string): LocationRef | null {
  const match = /^([afrd]):(\d+)$/.exec(value);
  return match ? { kind: KIND_OF_PREFIX[match[1]], id: Number(match[2]) } : null;
}

export const locationValues = (refs: readonly LocationRef[]): string[] => refs.map(locationValue);

/** Back from select values to refs: in order, deduplicated, junk dropped. */
export function locationRefs(values: readonly string[]): LocationRef[] {
  const seen = new Set<string>();
  const refs: LocationRef[] = [];
  for (const value of values) {
    const ref = parseLocationValue(value);
    if (!ref) continue;
    const key = locationValue(ref);
    if (seen.has(key)) continue;
    seen.add(key);
    refs.push(ref);
  }
  return refs;
}

/** The API's id lists for a scope (sorted, deduplicated). */
export function locationIdsByKind(refs: readonly LocationRef[]): {
  addressIds: number[];
  floorIds: number[];
  roomIds: number[];
} {
  const ids = (kind: PickerKind) =>
    [...new Set(refs.filter((r) => r.kind === kind).map((r) => r.id))].sort((a, b) => a - b);
  return { addressIds: ids('address'), floorIds: ids('floor'), roomIds: ids('room') };
}

const natural = (a: string, b: string) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
const clean = (text: string | null | undefined) => (text ?? '').trim();
const key = (id: number | string | null | undefined) => (id == null ? '' : String(id));

interface AddressInfo {
  id: string;
  name: string;
  short: string;
}
interface FloorInfo {
  id: string;
  name: string;
  display: string;
  addressId: string;
}

/** "Townhouse · 26605" — or just the number when the room has no description. */
export function roomTitle(room: Pick<PickerRoom, 'name' | 'description'>): string {
  const description = clean(room.description);
  const number = clean(room.name);
  return description && description !== number ? `${description} · ${number}` : number || description;
}

/** One searchable list over the three levels, grouped by the level above
 *  the highest one offered:
 *  - `['room']`: rooms under "Lunta Street 26 · Floor 6";
 *  - `['floor']` (and `['floor','room']`): floors (with their rooms under them) per address;
 *  - `['address']`: a flat list of addresses;
 *  - `['door']`: doors under "Lunta Street 26 · Floor 6" (the floor they sit on,
 *    or their room's floor), doors with no place last; with other kinds, doors
 *    join the tree under their floor (else their address);
 *  - `['address','floor','room']`: per address, the address itself ("All floors"),
 *    then its floors, each with its rooms under it.
 *  Addresses `addresses` lists also lend their short name ("L26") to the search
 *  of every floor and room under them. */
export function buildLocationOptions(
  kinds: readonly PickerKind[],
  { addresses = [], floors = [], rooms = [], doors = [] }: LocationPickerData,
): LocationOptionGroup[] {
  const offers = (kind: PickerKind) => kinds.includes(kind);

  // Address index: the listed addresses carry the short name; nested ones fill gaps.
  const addressIndex = new Map<string, AddressInfo>();
  const noteAddress = (a: AddressLike | null | undefined) => {
    if (!a) return;
    const id = key(a.id);
    const known = addressIndex.get(id);
    const short = clean(a.short_name) || known?.short || '';
    addressIndex.set(id, { id, name: clean(a.name) || known?.name || '', short });
  };
  addresses.forEach(noteAddress);
  floors.forEach((f) => noteAddress(f.address));
  rooms.forEach((r) => noteAddress(r.floor?.address));

  // Floor index: listed floors plus every floor a room is on.
  const floorIndex = new Map<string, FloorInfo>();
  const noteFloor = (f: { id: number | string; name: string }, addressId: string) => {
    const id = key(f.id);
    if (floorIndex.has(id)) return;
    floorIndex.set(id, { id, name: clean(f.name), display: floorDisplayName(f.name ?? ''), addressId });
  };
  floors.forEach((f) => noteFloor(f, key(f.address?.id ?? f.address_id)));
  rooms.forEach((r) => r.floor && noteFloor(r.floor, key(r.floor.address?.id)));
  // Doors lend their places only to a picker that offers doors.
  const offeredDoors = offers('door') ? doors : [];
  for (const d of offeredDoors) {
    noteAddress(d.address);
    noteAddress(d.floor?.address);
    noteAddress(d.room?.floor?.address);
    const doorFloor = d.floor ?? d.room?.floor;
    if (doorFloor) noteFloor(doorFloor, key(doorFloor.address?.id ?? d.address?.id));
  }

  const addressOf = (id: string): AddressInfo | undefined => addressIndex.get(id);
  const addressSearch = (a: AddressInfo | undefined) => (a ? [a.name, a.short] : []);
  const search = (parts: string[]) => parts.filter(Boolean).join(' ').toLowerCase();

  const roomOption = (room: PickerRoom, depth: 0 | 1, parent?: string, withFloor = false): LocationOption => {
    const floor = room.floor ? floorIndex.get(key(room.floor.id)) : undefined;
    const address = floor ? addressOf(floor.addressId) : undefined;
    const description = clean(room.description);
    const number = clean(room.name);
    const place = [address?.name, floor?.display].filter(Boolean).join(', ');
    const title = roomTitle(room);
    const hasDescription = !!description && description !== number;
    const muted = [hasDescription ? number : '', withFloor ? floor?.display ?? '' : ''].filter(Boolean).join(' · ');
    return {
      value: locationValue({ kind: 'room', id: Number(room.id) }),
      kind: 'room',
      id: Number(room.id),
      label: place ? `${title} — ${place}` : title,
      primary: hasDescription ? description : number || description,
      secondary: muted || undefined,
      depth,
      parent,
      search: search([description, number, floor?.name ?? '', floor?.display ?? '', ...addressSearch(address)]),
    };
  };

  /** Where a door sits: its floor (else its room's), and that floor's address (else its own). */
  const doorPlace = (door: PickerDoor): { floor?: FloorInfo; address?: AddressInfo } => {
    const floorRef = door.floor ?? door.room?.floor;
    const floor = floorRef ? floorIndex.get(key(floorRef.id)) : undefined;
    const addressId = key(door.address?.id ?? floorRef?.address?.id ?? floor?.addressId);
    return { floor, address: addressId ? addressOf(addressId) : undefined };
  };

  const doorOption = (door: PickerDoor, depth: 0 | 1, parent?: string): LocationOption => {
    const { floor, address } = doorPlace(door);
    const description = clean(door.description);
    const roomName = door.room ? roomTitle(door.room) : '';
    const place = [address?.name, floor?.display, roomName].filter(Boolean).join(', ');
    const name = clean(door.name) || `Door ${door.id}`;
    return {
      value: locationValue({ kind: 'door', id: Number(door.id) }),
      kind: 'door',
      id: Number(door.id),
      label: place ? `${name} — ${place}` : name,
      primary: name,
      secondary: roomName || undefined,
      depth,
      parent,
      search: search([
        name,
        description,
        door.room?.name ?? '',
        door.room?.description ?? '',
        floor?.name ?? '',
        floor?.display ?? '',
        ...addressSearch(address),
      ]),
    };
  };

  const floorOption = (floor: FloorInfo): LocationOption => {
    const address = addressOf(floor.addressId);
    return {
      value: locationValue({ kind: 'floor', id: Number(floor.id) }),
      kind: 'floor',
      id: Number(floor.id),
      label: address?.name ? `${address.name}, ${floor.display}` : floor.display,
      primary: floor.display,
      depth: 0,
      search: search([floor.name, floor.display, ...addressSearch(address)]),
    };
  };

  const addressOption = (address: AddressInfo, inTree: boolean): LocationOption => ({
    value: locationValue({ kind: 'address', id: Number(address.id) }),
    kind: 'address',
    id: Number(address.id),
    label: address.name,
    primary: inTree ? 'All floors' : address.name,
    secondary: !inTree && address.short && address.short !== address.name ? address.short : undefined,
    depth: 0,
    search: search(addressSearch(address)),
  });

  const sortedRooms = [...rooms].sort((a, b) => natural(clean(a.name), clean(b.name)));
  const roomsOnFloor = (floorId: string) => sortedRooms.filter((r) => key(r.floor?.id) === floorId);
  const sortedDoors = [...offeredDoors].sort((a, b) => natural(clean(a.name), clean(b.name)));
  const doorsOnFloor = (floorId: string) => sortedDoors.filter((d) => doorPlace(d).floor?.id === floorId);

  // Two addresses with the same name (two sites, an "Annex" per building) are
  // told apart in the group headings: "Annex (L26)", else "Annex (#90)".
  const nameCounts = new Map<string, number>();
  for (const a of addressIndex.values()) nameCounts.set(a.name.toLowerCase(), (nameCounts.get(a.name.toLowerCase()) ?? 0) + 1);
  const headingName = (a: AddressInfo | undefined): string => {
    if (!a) return '';
    if ((nameCounts.get(a.name.toLowerCase()) ?? 0) < 2) return a.name;
    return a.short && a.short !== a.name ? `${a.name} (${a.short})` : `${a.name} (#${a.id})`;
  };

  // Doors only: one group per address · floor; doors with no place last.
  if (offers('door') && kinds.length === 1) {
    const groups = new Map<string, { heading: string; address: string; floor: string; items: LocationOption[] }>();
    for (const door of sortedDoors) {
      const { floor, address } = doorPlace(door);
      const groupKey = floor || address ? `${address?.id ?? ''}|${floor?.id ?? ''}` : NO_PLACE_KEY;
      if (!groups.has(groupKey)) {
        groups.set(groupKey, {
          heading: floor || address ? [headingName(address), floor?.display].filter(Boolean).join(' · ') : NO_PLACE,
          address: headingName(address),
          floor: floor?.name ?? '',
          items: [],
        });
      }
      groups.get(groupKey)!.items.push(doorOption(door, 0));
    }
    return uniqueHeadings(
      [...groups.entries()]
        .sort(
          ([ka, a], [kb, b]) =>
            lastIf(ka === NO_PLACE_KEY, kb === NO_PLACE_KEY) || natural(a.address, b.address) || natural(a.floor, b.floor),
        )
        .map(([, { heading, items }]) => ({ group: heading, items })),
    );
  }

  // Rooms only: one group per address · floor; rooms without a floor last.
  if (!offers('address') && !offers('floor') && !offers('door')) {
    const groups = new Map<string, { heading: string; address: string; floor: string; items: LocationOption[] }>();
    for (const room of sortedRooms) {
      const floor = room.floor ? floorIndex.get(key(room.floor.id)) : undefined;
      const address = floor ? addressOf(floor.addressId) : undefined;
      const groupKey = floor ? `${floor.addressId}|${floor.id}` : NO_FLOOR_KEY;
      if (!groups.has(groupKey)) {
        groups.set(groupKey, {
          heading: floor ? [headingName(address), floor.display].filter(Boolean).join(' · ') : NO_FLOOR,
          address: headingName(address),
          floor: floor?.name ?? '',
          items: [],
        });
      }
      groups.get(groupKey)!.items.push(roomOption(room, 0));
    }
    return uniqueHeadings(
      [...groups.entries()]
        .sort(
          ([ka, a], [kb, b]) =>
            lastIf(ka === NO_FLOOR_KEY, kb === NO_FLOOR_KEY) || natural(a.address, b.address) || natural(a.floor, b.floor),
        )
        .map(([, { heading, items }]) => ({ group: heading, items })),
    );
  }

  // Addresses only: a flat list.
  if (offers('address') && !offers('floor') && !offers('room') && !offers('door')) {
    const items = addresses
      .map((a) => addressIndex.get(key(a.id))!)
      .sort((a, b) => natural(a.name, b.name))
      .map((a) => addressOption(a, false));
    return items.length ? [{ group: '', items }] : [];
  }

  // A tree per address: [the address], its floors, each floor's rooms under it.
  const perAddress = new Map<string, LocationOption[]>();
  const push = (groupKey: string, option: LocationOption) => {
    if (!perAddress.has(groupKey)) perAddress.set(groupKey, []);
    perAddress.get(groupKey)!.push(option);
  };
  if (offers('address')) {
    addresses.forEach((a) => push(key(a.id), addressOption(addressIndex.get(key(a.id))!, true)));
  }
  const floorsInOrder = [...floorIndex.values()].sort((a, b) => natural(a.name, b.name));
  for (const floor of floorsInOrder) {
    const floorRow = offers('floor') ? floorOption(floor) : undefined;
    const floorRooms = offers('room') ? roomsOnFloor(floor.id) : [];
    const floorDoors = doorsOnFloor(floor.id);
    // A floor listed only because a room or door is on it is offered only when those are.
    const listed = floors.some((f) => key(f.id) === floor.id);
    const showFloorRow = !!floorRow && (listed || floorRooms.length > 0 || floorDoors.length > 0);
    if (floorRow && showFloorRow) push(floor.addressId, floorRow);
    const parentRow = showFloorRow ? floorRow : undefined;
    for (const room of floorRooms) {
      push(floor.addressId, roomOption(room, parentRow ? 1 : 0, parentRow?.value, !floorRow));
    }
    for (const door of floorDoors) {
      push(floor.addressId, doorOption(door, parentRow ? 1 : 0, parentRow?.value));
    }
  }
  if (offers('room')) {
    sortedRooms.filter((r) => !r.floor).forEach((room) => push(NO_FLOOR_KEY, roomOption(room, 0)));
  }
  // Doors on no floor sit under their address, else with the rows that have no place.
  for (const door of sortedDoors.filter((d) => !doorPlace(d).floor)) {
    const { address } = doorPlace(door);
    push(address ? address.id : NO_FLOOR_KEY, doorOption(door, 0));
  }
  // Named addresses first, then floors with no known address, then rooms with no floor.
  const rank = (groupKey: string) => (groupKey === NO_FLOOR_KEY ? 2 : addressOf(groupKey)?.name ? 0 : 1);
  return uniqueHeadings(
    [...perAddress.entries()]
      .sort(([ka], [kb]) => rank(ka) - rank(kb) || natural(headingName(addressOf(ka)), headingName(addressOf(kb))))
      .map(([groupKey, items]) => ({
        group: groupKey === NO_FLOOR_KEY ? NO_FLOOR : headingName(addressOf(groupKey)) || NO_ADDRESS,
        items,
      })),
  );
}

const NO_FLOOR = 'No floor';
const NO_FLOOR_KEY = '\u0000no-floor';
const NO_ADDRESS = 'Other floors';
const NO_PLACE = 'No location';
const NO_PLACE_KEY = '\u0000no-place';

/** Sort helper: the flagged side goes last. */
const lastIf = (a: boolean, b: boolean): number => Number(a) - Number(b);

/** Mantine keys option groups by their heading: never let two share one. */
function uniqueHeadings(groups: LocationOptionGroup[]): LocationOptionGroup[] {
  const seen = new Map<string, number>();
  return groups.map((g) => {
    const n = (seen.get(g.group) ?? 0) + 1;
    seen.set(g.group, n);
    return n === 1 ? g : { ...g, group: `${g.group} (${n})` };
  });
}

/** The words a search text is split into, and the haystack too. */
const words = (text: string) => text.split(/[\s,·›—()]+/).filter(Boolean);

/**
 * Every word of `query` must hit: a word of the option's text that starts
 * with it ("town" → Townhouse, "6" → floor 6, "L26" → the short name), or —
 * for words of three characters or more — any part of it ("605" → 26605).
 * Short words match word starts only, so "L26 6" means floor 6 at L26 and
 * not every room whose number has a 6 in it.
 */
export function matchesLocationQuery(option: Pick<LocationOption, 'search'>, query: string): boolean {
  const tokens = words(query.toLowerCase());
  if (tokens.length === 0) return true;
  const haystack = words(option.search);
  return tokens.every((t) => haystack.some((w) => w.startsWith(t)) || (t.length >= 3 && option.search.includes(t)));
}

/** The values a search keeps visible: the hits plus the rows above them in
 *  the tree (so a room shows under its floor); null = no search, all. */
export function visibleLocationValues(options: readonly LocationOption[], query: string): Set<string> | null {
  if (words(query).length === 0) return null;
  const byValue = new Map(options.map((o) => [o.value, o]));
  const visible = new Set<string>();
  for (const option of options) {
    if (!matchesLocationQuery(option, query)) continue;
    for (let o: LocationOption | undefined = option; o && !visible.has(o.value); o = o.parent ? byValue.get(o.parent) : undefined) {
      visible.add(o.value);
    }
  }
  return visible;
}

/** The groups as a search leaves them (empty groups dropped). */
export function filterLocationGroups(groups: readonly LocationOptionGroup[], query: string): LocationOptionGroup[] {
  const visible = visibleLocationValues(groups.flatMap((g) => g.items), query);
  if (!visible) return [...groups];
  return groups
    .map((g) => ({ group: g.group, items: g.items.filter((i) => visible.has(i.value)) }))
    .filter((g) => g.items.length > 0);
}

/** Mantine `data`: grouped, or flat for the ungrouped address list. */
export function toSelectData(
  groups: readonly LocationOptionGroup[],
): { value: string; label: string }[] | { group: string; items: { value: string; label: string }[] }[] {
  const item = (o: LocationOption) => ({ value: o.value, label: o.label });
  if (groups.length === 1 && groups[0].group === '') return groups[0].items.map(item);
  return groups.map((g) => ({ group: g.group, items: g.items.map(item) }));
}

/** One line for a scope list (a signage board): the places it covers, or the whole site. */
export function scopeSummary(scopeLabels: readonly string[] | undefined, siteName?: string): string {
  if (scopeLabels && scopeLabels.length > 0) return scopeLabels.join(' · ');
  return siteName ? `Whole site (${siteName})` : 'Whole site';
}
